import {
  FaIcon,
  type IconDefinition,
  faCalendar,
  faCalendarCheck,
  faXmark,
} from '@/lib/fa'
import * as React from 'react'
import { createPortal } from 'react-dom'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

export type YearMonthValue = {
  year: number
  month: number | null // 1-12 or null
}

export type YearMonthPickerButtonProps = {
  label?: string
  placeholder?: string
  iconAlign?: 'left' | 'right'
  fullWidth?: boolean
  size?: 'small' | 'large'
  highlightSelected?: boolean
  clearable?: boolean

  /** controlled/uncontrolled */
  value?: YearMonthValue
  defaultValue?: YearMonthValue
  handleChange?: (value: YearMonthValue) => void

  showClearInPopup?: boolean
  showConfirmButtons?: boolean
  closeOnSelect?: boolean // default true

  icon?: IconDefinition
  iconSelected?: IconDefinition
  deleteIcon?: IconDefinition
}

const MONTH_SHORT_KEYS = [
  'common.month-jan-short',
  'common.month-feb-short',
  'common.month-mar-short',
  'common.month-apr-short',
  'common.month-may-short',
  'common.month-jun-short',
  'common.month-jul-short',
  'common.month-aug-short',
  'common.month-sep-short',
  'common.month-oct-short',
  'common.month-nov-short',
  'common.month-dec-short',
]

const MONTH_LONG_KEYS = [
  'monthName.january',
  'monthName.february',
  'monthName.march',
  'monthName.april',
  'monthName.may',
  'monthName.june',
  'monthName.july',
  'monthName.august',
  'monthName.september',
  'monthName.october',
  'monthName.november',
  'monthName.december',
]

const getCurrentYear = () => new Date().getFullYear()

const clampYear = (year: number) => {
  if (Number.isNaN(year)) return getCurrentYear()
  return Math.max(1900, Math.min(2100, year))
}

export const YearMonthPickerButton: React.FC<YearMonthPickerButtonProps> = ({
  label,
  placeholder,
  iconAlign = 'left',
  fullWidth = false,
  size = 'large',
  highlightSelected = false,
  clearable = true,

  value,
  defaultValue,
  handleChange,

  showClearInPopup = false,
  showConfirmButtons = false,
  closeOnSelect = true,

  icon = faCalendar,
  iconSelected = faCalendarCheck,
  deleteIcon = faXmark,
}) => {
  const { t } = useLocalize()
  const monthLabels = MONTH_SHORT_KEYS.map((key) => t(key))
  const monthLabelsLong = MONTH_LONG_KEYS.map((key) => t(key))

  const formatSelection = (val: YearMonthValue) => {
    if (!val.month) return ''
    const label = monthLabelsLong[val.month - 1]
    return label ? `${val.year} • ${label}` : ''
  }

  const resolvedPlaceholder = placeholder ?? t('configuration.select-year-month')

  const isControlled = value !== undefined

  const initialInternal: YearMonthValue =
    defaultValue ?? { year: getCurrentYear(), month: null }

  const [internalValue, setInternalValue] =
    React.useState<YearMonthValue>(initialInternal)

  const selected: YearMonthValue = isControlled ? value! : internalValue

  const setSelectedValue = React.useCallback(
    (next: YearMonthValue) => {
      if (!isControlled) setInternalValue(next)
      handleChange?.(next)
    },
    [isControlled, handleChange],
  )

  const [open, setOpen] = React.useState(false)
  const [draft, setDraft] = React.useState<YearMonthValue>(selected)

  // Keep draft in sync when not using confirm, and also when selection changes externally
  React.useEffect(() => {
    if (!showConfirmButtons) {
      setDraft(selected)
    }
  }, [selected, showConfirmButtons])

  // When opening with confirm buttons, reset draft to current selected
  React.useEffect(() => {
    if (open && showConfirmButtons) setDraft(selected)
  }, [open, showConfirmButtons, selected])

  const containerRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
  })

  const hasSelected = selected.month != null

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const willOpen = !open

    if (willOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      })
    }

    setOpen(willOpen)
  }

  const doReset = React.useCallback(() => {
    const reset: YearMonthValue = { year: getCurrentYear(), month: null }
    setSelectedValue(reset)
    setDraft(reset)
    setOpen(false)
  }, [setSelectedValue])

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    doReset()
  }

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node
      if (containerRef.current && !containerRef.current.contains(target)) {
        if (showConfirmButtons) setDraft(selected)
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [selected, showConfirmButtons])

  const setYear = (year: number) =>
    setDraft((prev) => ({ ...prev, year: clampYear(year) }))

  const selectMonth = (m: number) => {
    const next: YearMonthValue = {
      year: draft.year,
      month: draft.month === m ? null : m,
    }

    if (!showConfirmButtons) {
      setSelectedValue(next)
      setDraft(next)
      if (closeOnSelect) setOpen(false)
      return
    }

    setDraft(next)
    if (closeOnSelect) {
      setSelectedValue(next)
      setOpen(false)
    }
  }

  const handleCancel = () => {
    setDraft(selected)
    setOpen(false)
  }

  const handleConfirm = () => {
    setSelectedValue(draft)
    setOpen(false)
  }

  const handleClearInPopup = () => {
    if (!showConfirmButtons) {
      doReset()
      return
    }
    setDraft((p) => ({ ...p, month: null }))
  }

  const Dropdown = (
    <div
      ref={containerRef}
      className="tw-z-60 tw-absolute tw-min-w-[320px] tw-rounded tw-border tw-border-neutral/20 tw-bg-base-100 tw-p-3 tw-shadow-md"
      style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
    >
      <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-gap-3">
        <div className="tw-flex tw-items-center tw-gap-2">
          <button
            type="button"
            className="tw-rounded tw-border tw-border-neutral/20 tw-px-2 tw-py-1 hover:tw-bg-[#F5F5F5]"
            onClick={() => setYear(draft.year - 1)}
            aria-label="Previous year"
          >
            ‹
          </button>

          <input
            className="tw-w-[96px] tw-rounded tw-border tw-border-neutral/20 tw-px-2 tw-py-1"
            type="number"
            value={draft.year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
          />

          <button
            type="button"
            className="tw-rounded tw-border tw-border-neutral/20 tw-px-2 tw-py-1 hover:tw-bg-[#F5F5F5]"
            onClick={() => setYear(draft.year + 1)}
            aria-label="Next year"
          >
            ›
          </button>
        </div>

        {showClearInPopup && (
          <button
            type="button"
            className="tw-rounded tw-border tw-border-neutral/20 tw-px-2 tw-py-1 tw-text-sm hover:tw-bg-[#F5F5F5]"
            onClick={handleClearInPopup}
          >
            {t('configuration.clear')}
          </button>
        )}
      </div>

      <div className="tw-mb-3 tw-grid tw-grid-cols-3 tw-gap-2">
        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
          const isOn = draft.month === m
          return (
            <button
              key={m}
              type="button"
              onClick={() => selectMonth(m)}
              className={[
                'tw-rounded tw-border tw-px-2 tw-py-2 tw-text-left tw-text-sm',
                isOn
                  ? 'tw-border-primary tw-bg-[#F1F4FF] tw-text-primary'
                  : 'tw-border-neutral/20 hover:tw-bg-[#F5F5F5]',
              ].join(' ')}
            >
              {monthLabels[m - 1]}
            </button>
          )
        })}
      </div>

      {showConfirmButtons && (
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
          <button
            type="button"
            className="tw-rounded tw-border tw-border-neutral/20 tw-px-3 tw-py-2 tw-text-sm hover:tw-bg-[#F5F5F5]"
            onClick={handleCancel}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="tw-rounded tw-border tw-border-primary tw-bg-[#F1F4FF] tw-px-3 tw-py-2 tw-text-sm tw-text-primary hover:tw-bg-[#E7ECFF]"
            onClick={handleConfirm}
          >
            {t('common.apply')}
          </button>
        </div>
      )}
    </div>
  )

  const buttonText =
    selected.month != null ? formatSelection(selected) : label ? label : ''

  const showPlaceholder = !buttonText

  return (
    <div className="tw-relative tw-z-50 tw-inline-block">
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        className={[
          'tw-relative tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded tw-border tw-px-2 tw-text-base-content hover:tw-bg-[#F5F5F5]',
          open ? 'tw-border-2 tw-border-primary' : '',
          highlightSelected && hasSelected
            ? 'tw-border-primary tw-bg-[#F1F4FF]'
            : 'tw-border-neutral/20 tw-bg-transparent',
          iconAlign === 'right' ? 'tw-flex-row-reverse' : 'tw-flex-row',
          fullWidth ? 'tw-w-full' : '',
          size === 'small' ? 'tw-py-1' : 'tw-py-2',
        ].join(' ')}
      >
        <FaIcon
          icon={highlightSelected && hasSelected ? iconSelected : icon}
          className="tw-h-4 tw-w-4"
        />

        <div className="tw-flex tw-items-center tw-gap-2">
          {showPlaceholder ? (
            <p className="tw-text-sm tw-opacity-70">{resolvedPlaceholder}</p>
          ) : (
            <p
              className={`tw-text-sm ${highlightSelected && hasSelected ? 'tw-text-primary' : ''}`}
            >
              {buttonText}
            </p>
          )}

          {clearable && hasSelected && deleteIcon && (
            <FaIcon
              icon={deleteIcon}
              onClick={handleDeleteClick}
              className="tw-h-4 tw-w-4 tw-cursor-pointer tw-opacity-70 hover:tw-opacity-100"
            />
          )}

          {clearable && hasSelected && !deleteIcon && (
            <button
              type="button"
              onClick={handleDeleteClick}
              className="tw-text-xs tw-opacity-70 hover:tw-opacity-100"
              aria-label="Clear selection"
            >
              ✕
            </button>
          )}
        </div>
      </button>

      {open && createPortal(Dropdown, document.body)}
    </div>
  )
}
